import { FC, memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { IApiConnectedAccount } from '../../types';
import { T_KEYS } from '../../constants';

// Icons
const ImportIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" />
  </svg>
);

const RefreshIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M23 4v6h-6M1 20v-6h6" />
    <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
  </svg>
);

const SettingsIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z" />
  </svg>
);

const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

// Back arrow icon
const BackArrowIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ReconnectIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M6.66667 2.66667H4.66667C3.56667 2.66667 2.66667 3.56667 2.66667 4.66667V11.3333C2.66667 12.4333 3.56667 13.3333 4.66667 13.3333H11.3333C12.4333 13.3333 13.3333 12.4333 13.3333 11.3333V9.33333" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M12 2L14 4L12 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M8 8L14 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

interface IBookingAccountSectionProps {
  account: IApiConnectedAccount;
  selectedSubAccountIndex: number;
  onSelectSubAccount: (index: number) => void;
  onImportNewUnits: () => void;
  onRefreshSync: () => void | Promise<void>;
  onBack: () => void;
  onAddProperty: () => void;
  onOpenSettings: () => void;
  onReconnect: () => void | Promise<void>;
  isRefreshing?: boolean;
  isReconnecting?: boolean;
  notImportedCount?: number;
}

/**
 * BookingAccountSection Component
 * 
 * Displays the Booking account section matching Figma design:
 * - Account header card with title, status badge, last sync, and action buttons
 * - Hotel tabs below the header card (outside the card)
 * - Property info row with hotel name, Property ID, and action buttons
 */
const BookingAccountSection: FC<IBookingAccountSectionProps> = ({
  account,
  selectedSubAccountIndex,
  onSelectSubAccount,
  onImportNewUnits,
  onRefreshSync,
  onBack,
  onAddProperty,
  onOpenSettings,
  onReconnect,
  isRefreshing = false,
  isReconnecting = false,
  notImportedCount,
}) => {
  const { t } = useTranslation();
  // Get subAccounts or use the account itself if no subAccounts
  // For Connection Type 2 (Multi-unit), the account itself is the property
  // For Connection Type 1 (Single), subAccounts contains the properties
  const subAccounts = useMemo(() => {
    if (account.subAccounts && account.subAccounts.length > 0) {
      return account.subAccounts;
    }
    // Fallback to account itself for Connection Type 2 or when no subAccounts
    return [account];
  }, [account]);

  // Get selected subAccount
  const selectedSubAccount = subAccounts[selectedSubAccountIndex] || subAccounts[0];

  // Format last sync time
  const lastSyncTime = useMemo(() => {
    if (!account?.updateAt) return null;
    const date = new Date(account.updateAt);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return t(T_KEYS.LAST_SYNC_NOW);
    if (diffMins < 60) return t(T_KEYS.LAST_SYNC_MINUTES, { minutes: diffMins });
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return t(T_KEYS.LAST_SYNC_HOURS, { hours: diffHours });
    
    const diffDays = Math.floor(diffHours / 24);
    return t(T_KEYS.LAST_SYNC_DAYS, { days: diffDays });
  }, [account]);

  return (
    <SectionContainer>
      {/* Account Header Card */}
      <AccountHeaderCard>
        <AccountTitle>
          {account.channelName}
        </AccountTitle>
        <AccountHeaderInfo>
          <div>
            <StatusBadge $isActive={account.isChannelActive}>
              <StatusDot $isActive={account.isChannelActive} />
              {account.isChannelActive ? t(T_KEYS.CONNECTED) : t(T_KEYS.DISCONNECTED)}
            </StatusBadge>
            {lastSyncTime && (
              <LastSync>
                • {lastSyncTime}
              </LastSync>
            )}
          </div>
          <AccountHeaderActions>
            {/* Show reconnect button for single connection type (has subAccounts) when inactive */}
            {!account.isChannelActive && account.subAccounts && account.subAccounts.length > 0 ? (
              <ReconnectButton onClick={onReconnect} disabled={isReconnecting}>
                {isReconnecting ? t(T_KEYS.RECONNECTING) || 'Reconnecting...' : t(T_KEYS.RECONNECT_ACCOUNT)}
                {!isReconnecting && <ReconnectIcon />}
              </ReconnectButton>
            ) : (
              <AddPropertyButton onClick={onAddProperty}>
                <PlusIcon />
                {t(T_KEYS.ADD_NEW_PROPERTY_BUTTON)}
              </AddPropertyButton>
            )}
            <SettingsButton onClick={onOpenSettings}>
              <SettingsIcon />
            </SettingsButton>
          </AccountHeaderActions>
        </AccountHeaderInfo>
      </AccountHeaderCard>

      {/* Hotel Tabs (SubAccounts) - Outside the card */}
      {/* Only show tabs when there are actual subAccounts (Connection Type 1) */}
      {account.subAccounts && account.subAccounts.length > 0 && (
        <HotelTabsRow>
          <HotelTabs>
            {subAccounts.map((subAccount, index) => {
              const isSelected = index === selectedSubAccountIndex;
              // Use channelId as the tab name (property ID)
              const displayName = subAccount.channelName 
                || t(T_KEYS.PROPERTY_LABEL) + ` ${index + 1}`;
              
              return (
                <HotelTab
                  key={subAccount.id}
                  $isSelected={isSelected}
                  onClick={() => onSelectSubAccount(index)}
                >
                  {displayName} {selectedSubAccount.channelId || 'N/A'}
                </HotelTab>
              );
            })}
          </HotelTabs>
        </HotelTabsRow>
      )}

      {/* Property Info Row with Actions */}
      {selectedSubAccount && (
        <PropertyInfoRow>
          <PropertyInfo>
            <PropertyName>
              {selectedSubAccount.channelName || t(T_KEYS.PROPERTY_LABEL)}
            </PropertyName>
            <PropertyId>
              (Property ID: {selectedSubAccount.channelId || 'N/A'})
            </PropertyId>
          </PropertyInfo>
          <PropertyActions>
            {/* Show reconnect button for multi connection type (no subAccounts) when inactive */}
            {!account.isChannelActive && (!account.subAccounts || account.subAccounts.length === 0) ? (
              <ReconnectButton onClick={onReconnect} disabled={isReconnecting}>
                {isReconnecting ? t(T_KEYS.RECONNECTING) || 'Reconnecting...' : t(T_KEYS.RECONNECT_ACCOUNT)}
                {!isReconnecting && <ReconnectIcon />}
              </ReconnectButton>
            ) : (
              <>
                <ActionButton disabled={!account.isChannelActive} onClick={onImportNewUnits}>
                  <ImportIcon />
                  {notImportedCount !== undefined && notImportedCount > 0
                    ? t(T_KEYS.IMPORT_NEW_UNITS_WITH_COUNT, { count: notImportedCount })
                    : t(T_KEYS.IMPORT_NEW_UNITS_BUTTON)}
                </ActionButton>
                <ActionButton disabled={!account.isChannelActive} onClick={onRefreshSync}>
                  <RefreshIcon />
                  {t(T_KEYS.REFRESH_SYNC_BUTTON)}
                </ActionButton>
              </>
            )}
          </PropertyActions>
          
        </PropertyInfoRow>
      )}
    </SectionContainer>
  );
};

export default memo(BookingAccountSection);

// Styled Components
const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const AccountHeaderCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px 20px;
  background: white;
  border: 1px solid #e6e6e6;
  border-radius: 16px;
`;

const AccountHeaderInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
`;

const AccountTitle = styled.h3`
  font-family: 'IBM Plex Sans Arabic', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #262626;
  margin: 0;
`;

const StatusBadge = styled.span<{ $isActive: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 100px;
  font-family: 'IBM Plex Sans Arabic', sans-serif;
  font-size: 12px;
  font-weight: 500;
  background-color: ${({ $isActive }) => ($isActive ? '#e6f7ed' : '#fef3f2')};
  color: ${({ $isActive }) => ($isActive ? '#12b76a' : '#f04438')};
`;

const StatusDot = styled.span<{ $isActive: boolean }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: ${({ $isActive }) => ($isActive ? '#12b76a' : '#f04438')};
`;

const LastSync = styled.span`
  font-family: 'IBM Plex Sans Arabic', sans-serif;
  font-size: 14px;
  color: #9ca3af;
`;

const AccountHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const AddPropertyButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border: 1px solid #5B3FFF;
  border-radius: 999px;
  background: white;
  font-family: 'IBM Plex Sans Arabic', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #5b2ff9;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f5f3ff;
  }
  
  svg {
    stroke: #5b2ff9;
  }
`;

const ReconnectButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 17px;
  background: white;
  color: #5b2ff9;
  border: 1px solid #5b2ff9;
  border-radius: 20px;
  font-family: 'IBM Plex Sans Arabic', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f5f3ff;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const SettingsButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: 1px solid #e6e6e6;
  border-radius: 999px;
  background: white;
  color: #666;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f5f5f5;
    border-color: #d0d0d0;
  }
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #262626;
  cursor: pointer;
  transition: all 0.2s ease;
  
  [dir="rtl"] & {
    transform: rotate(180deg);
  }
  
  &:hover {
    background: #f5f5f5;
  }
`;

const HotelTabsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
`;

const HotelTabs = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const HotelTab = styled.button<{ $isSelected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  padding: 8px 16px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  font-family: 'IBM Plex Sans Arabic', sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 20px;
  transition: all 0.2s ease;
  
  background-color: ${({ $isSelected }) => ($isSelected ? '#eeebff' : '#f5f5f5')};
  color: ${({ $isSelected }) => ($isSelected ? '#262626' : '#666666')};
  
  &:hover {
    background-color: ${({ $isSelected }) => ($isSelected ? '#eeebff' : '#e8e8e8')};
  }
`;

const PropertyInfoRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: white;
  border: 1px solid #e6e6e6;
  border-radius: 16px;
`;

const PropertyInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PropertyName = styled.span`
  font-family: 'IBM Plex Sans Arabic', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #262626;
`;

const PropertyId = styled.span`
  font-family: 'IBM Plex Sans Arabic', sans-serif;
  font-size: 14px;
  color: #666;
`;

const PropertyActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border: 1px solid #e6e6e6;
  border-radius: 999px;
  background: white;
  font-family: 'IBM Plex Sans Arabic', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #262626;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f5f5f5;
    border-color: #d0d0d0;
  }
`;
